import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Icon } from "@/lib/icons";
import { ProgressRing } from "@/components/ProgressRing";
import { Trophy, Zap, CheckCircle2, ArrowRight } from "lucide-react";

export default function Challenges() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/challenges").then((r) => setChallenges(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const totalXp = challenges.reduce((a, c) => a + (c.xp_earned || 0), 0);
  const completed = challenges.filter((c) => c.completed).length;

  return (
    <div className="space-y-8" data-testid="challenges-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Entrepreneur Challenges</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Learn by doing. Earn your badges.</h1>
        <p className="text-slate-400 mt-2">Gamified, step-by-step challenges that turn business theory into real momentum. Free for everyone.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="eco-card p-5" data-testid="challenges-stat-xp">
          <div className="flex items-center justify-between"><span className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">Total XP</span><Zap size={18} className="text-amber-400" /></div>
          <div className="font-display text-3xl font-bold text-white mt-3">{totalXp}</div>
        </div>
        <div className="eco-card p-5" data-testid="challenges-stat-completed">
          <div className="flex items-center justify-between"><span className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">Completed</span><Trophy size={18} className="text-amber-400" /></div>
          <div className="font-display text-3xl font-bold text-white mt-3">{completed}<span className="text-slate-600 text-lg">/{challenges.length}</span></div>
        </div>
        <div className="eco-card p-5 col-span-2 sm:col-span-1">
          <div className="flex items-center justify-between"><span className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">Active</span><CheckCircle2 size={18} className="text-emerald-400" /></div>
          <div className="font-display text-3xl font-bold text-white mt-3">{challenges.filter((c) => c.enrolled && !c.completed).length}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="eco-card p-6 h-44 animate-pulse" />)
          : challenges.map((c) => (
              <Link key={c.id} to={`/challenges/${c.id}`} data-testid={`challenge-card-${c.id}`} className="eco-card eco-card-hover p-6 group flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: `${c.color}1a`, border: `1px solid ${c.color}33` }}>
                    <Icon name={c.icon} size={24} style={{ color: c.color }} />
                  </div>
                  {c.enrolled && (
                    <div className="text-right">
                      {c.completed
                        ? <span className="eco-tag flex items-center gap-1.5 text-emerald-300 border-emerald-400/20 bg-emerald-400/10"><CheckCircle2 size={13} /> Completed</span>
                        : <ProgressRing value={c.percent} size={44} color={c.color} />}
                    </div>
                  )}
                </div>
                <div className="mt-4 flex-1">
                  <h3 className="font-display text-lg font-semibold text-white">{c.title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{c.tagline}</p>
                  <p className="text-sm text-slate-400 mt-3 line-clamp-2">{c.description}</p>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>{c.duration_days} days</span>
                    <span className="flex items-center gap-1 text-amber-400"><Zap size={12} /> {c.xp_total} XP</span>
                  </div>
                  <span className="text-[#4d8bff] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">{c.enrolled ? "Continue" : "Start"} <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
      </div>
    </div>
  );
}
